import { useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Box, Flex, Stack, Text } from "@chakra-ui/react";
import { useCustomFormats } from "hooks/useCustomFormat";

export type Transaction = {
  year: number;
  amount: number;
  label: string;
};

const colorMap = {
  SFR: "#015AA4",
  "Mixed-Use": "#627785",
  Multifamily: "#6A97F0",
};

interface TransactionTrendsBarChartProps {
  title: string;
  transactions: Transaction[];
}

export function TransactionTrendsBarChart({ title, transactions }: TransactionTrendsBarChartProps) {
  const { currencySymbol } = useCustomFormats();

  const chartData = useMemo(() => {
    const years = [...new Set(transactions.map(t => t.year))];
    return years.map(year => {
      const yearData = transactions.filter(t => t.year === year);
      return {
        year,
        ...yearData.reduce((acc, curr) => {
          acc[curr.label] = curr.amount;
          return acc;
        }, {} as Record<string, number>),
      };
    });
  }, [transactions]);

  const chartWidth = useMemo(() => {
    const numberOfBars = chartData.length;
    const barWidth = 58;
    const gap = 20;
    const totalWidth = numberOfBars * barWidth + (numberOfBars - 1) * gap;
    return totalWidth + 200;
  }, [chartData]);

  const formatNumber = (value: number): string => {
    if (value >= 1000000) {
      return `${currencySymbol}${(value / 1000000).toFixed(0)}M`;
    }
    if (value >= 1000) {
      return `${currencySymbol}${(value / 1000).toFixed(0)}K`;
    }
    return `${currencySymbol}${value.toFixed(0)}`;
  };

  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div
          style={{
            backgroundColor: "white",
            padding: "10px",
            border: "1px solid #ccc",
            borderRadius: "4px",
          }}
        >
          <p style={{ margin: "0 0 5px 0" }}>Year: {label}</p>
          {payload.map((entry: any, index: number) => (
            <p
              key={index}
              style={{
                margin: "0",
                color: entry.color,
              }}
            >
              {entry.name}: {formatNumber(entry.value)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const ChartBox = ({ children }: { children: React.ReactNode }) => (
    <div style={{ boxShadow: "none", border: "1px solid #E2E2E2", borderRadius: 8, padding: 0 }}>
      {children}
    </div>
  );

  const ChartTitle = () => (
    <Text fontSize="md" fontWeight="medium" color="black">
      {title}
    </Text>
  );

  const ChartLegend = () => (
    <Flex gap={8} alignItems="center">
      {Object.entries(colorMap).map(([label, color]) => (
        <Flex key={label} alignItems="center" gap={2}>
          <Box bg={color} w={3} h={3} borderRadius="full" />
          <Text fontSize="xs" fontWeight="medium" color="#2E2C32">
            {label}
          </Text>
        </Flex>
      ))}
    </Flex>
  );

  const ChartContainer = () => (
    <div style={{ width: chartWidth, height: 228 }}>
      <BarChart
        data={chartData}
        margin={{
          top: 0,
          right: 0,
          left: 0,
          bottom: 0,
        }}
        barCategoryGap={20}
        width={chartWidth}
        height={228}
      >
        <CartesianGrid vertical={false} horizontal={true} stroke="#B9B7BD" strokeWidth={1} />
        <XAxis dataKey="year" />
        <YAxis
          tickFormatter={formatNumber}
          axisLine={false}
          tickLine={false}
          domain={[0, "auto"]}
        />
        <Tooltip content={<CustomTooltip />} />
        {Object.entries(colorMap).map(([label, color], index, array) => (
          <Bar
            key={label}
            dataKey={label}
            stackId="a"
            fill={color}
            name={label}
            radius={index === array.length - 1 ? [5, 5, 0, 0] : [0, 0, 0, 0]}
            stroke="none"
            strokeWidth={0}
            strokeOpacity={0}
            barSize={58}
          />
        ))}
      </BarChart>
    </div>
  );

  return (
    <ChartBox>
      <Stack gap={6}>
        <Stack px={6} py={4} gap={6}>
          <ChartTitle />
          <ChartLegend />
        </Stack>
        <ChartContainer />
      </Stack>
    </ChartBox>
  );
}
